import { useEffect, useState } from 'react';
import { Check, Download, Sparkles } from 'lucide-react';
import { ms } from '../services/calendarAdapters.js';

export default function SettingsScreen() {
  const [account, setAccount] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setAccount(ms.getAccount());
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const acc = await ms.signIn();
      setAccount(acc);
    } catch (e) {
      if (e.errorCode !== 'user_cancelled') {
        alert(e.message || e.errorMessage || 'Sign-in failed.');
      }
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    await ms.signOut();
    setAccount(null);
  };

  return (
    <div>
      <h1 className="h1">Settings</h1>
      <p className="muted" style={{ marginBottom: 20 }}>
        Where your scans go.
      </p>

      {/* Microsoft 365 — primary integration */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <h2 className="h2" style={{ marginBottom: 2 }}>Microsoft 365 / Outlook</h2>
            <div className="status status--success" style={{ marginBottom: 8 }}>
              <Sparkles size={12} /> Direct integration
            </div>
          </div>
        </div>
        {account ? (
          <>
            <p style={{ margin: '4px 0 12px' }}>
              Signed in as <strong>{account.username}</strong>
            </p>
            <p className="muted" style={{ marginBottom: 12, fontSize: 13 }}>
              Scans write directly into your Outlook calendar and contacts. One tap, done.
            </p>
            <button className="btn btn--ghost" onClick={disconnect}>Disconnect</button>
          </>
        ) : (
          <>
            <p className="muted" style={{ marginBottom: 12 }}>
              Sign in once to add events and contacts straight into your Outlook calendar.
            </p>
            <button className="btn btn--primary" onClick={connect} disabled={busy}>
              {busy ? 'Connecting…' : 'Connect Microsoft 365'}
            </button>
          </>
        )}
      </div>

      {/* Apple — works today via .ics/.vcf */}
      <div className="card" style={{ marginBottom: 12 }}>
        <h2 className="h2" style={{ marginBottom: 2 }}>Apple Calendar &amp; Contacts</h2>
        <div className="status status--info" style={{ marginBottom: 10 }}>
          <Check size={12} /> Working now
        </div>
        <p className="muted" style={{ fontSize: 14, margin: 0 }}>
          No setup needed. On the preview screen after a scan, tap <strong>Download .ics</strong> or
          <strong> .vcf</strong>. Your iPhone or Mac opens Apple Calendar / Contacts pre-filled — one tap
          to save to iCloud.
        </p>
      </div>

      {/* Google — works today via the same .ics/.vcf */}
      <div className="card" style={{ marginBottom: 12 }}>
        <h2 className="h2" style={{ marginBottom: 2 }}>Google Calendar &amp; Contacts</h2>
        <div className="status status--info" style={{ marginBottom: 10 }}>
          <Check size={12} /> Working now
        </div>
        <p className="muted" style={{ fontSize: 14, margin: 0 }}>
          Same as Apple — tap <strong>Download .ics</strong> or <strong>.vcf</strong> on the preview
          screen. On Android the file opens Google Calendar / Contacts directly; on desktop, drag the
          file onto calendar.google.com or contacts.google.com.
        </p>
      </div>

      {/* Other / anything else */}
      <div className="card" style={{ background: 'var(--surface-2)', border: 0 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <Download size={20} style={{ color: 'var(--text-muted)', flexShrink: 0, marginTop: 2 }} />
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Anything else?</div>
            <div className="muted" style={{ fontSize: 14 }}>
              The .ics and .vcf files we generate are universal formats. They work with Yahoo,
              Outlook desktop, Thunderbird, Fastmail, Proton — anything that handles calendars or
              contacts. Just download and open.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
